/**
 * Whether this browser draws a WebM video's alpha channel.
 *
 * <p>The reel celebrations ({@code <symbol>_win.webm}) and the pick'em
 * chest are transparent VP9 WebM. WebKit plays VP9 but ignores its alpha
 * plane, so the keyed-out background (black or green) shows as a box
 * around the symbol. That is Safari on the Mac and every browser on an
 * iPhone or iPad (Chrome, Firefox and Edge there are WebKit too), plus
 * the iOS app's web view. Blink (Chrome, Edge, Opera, Android) and
 * Gecko draw the alpha.
 *
 * <p>When this is false, {@link AssetLoader#getVideo} plays the clip's
 * HEVC-with-alpha copy (a {@code .mov}, which WebKit draws) where the
 * theme has one and the browser decodes HEVC ({@link hevcAlphaPreferred}),
 * and otherwise returns null, and every caller falls back to its
 * still-image path.
 */
export function webmAlphaSupported(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0,
): boolean {
  // iPhone, iPod and older iPads say so; iPadOS 13+ reports a Mac UA
  // but has a touch screen.
  if (/iPhone|iPad|iPod/.test(userAgent)) return false;
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return false;
  // Desktop Safari: WebKit without a Blink or Gecko token.
  if (/AppleWebKit/.test(userAgent) && !/Chrome\/|Chromium\/|Edg\/|OPR\//.test(userAgent)) return false;
  return true;
}

/**
 * Whether this browser should play a transparent clip's HEVC-with-alpha
 * copy (a {@code .mov}) in place of its WebM: it can't draw a WebM's alpha
 * (WebKit), and it decodes HEVC. WebKit draws HEVC's alpha (Safari 13 and
 * iOS 13 on); a browser that can do neither keeps the still image.
 */
export function hevcAlphaPreferred(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0,
  playsHevc: () => boolean = playsHevcHere,
): boolean {
  return !webmAlphaSupported(userAgent, maxTouchPoints) && playsHevc();
}

/** Whether a {@code <video>} here says it can decode HEVC ("hvc1"). */
function playsHevcHere(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    return document.createElement('video').canPlayType('video/mp4; codecs="hvc1"') !== '';
  } catch {
    return false;
  }
}

let cached: boolean | null = null;
let hevcCached: boolean | null = null;

/** {@link webmAlphaSupported} for this browser, worked out once. */
export function webmAlphaSupportedHere(): boolean {
  if (cached === null) cached = webmAlphaSupported();
  return cached;
}

/** {@link hevcAlphaPreferred} for this browser, worked out once. */
export function hevcAlphaPreferredHere(): boolean {
  if (hevcCached === null) hevcCached = hevcAlphaPreferred();
  return hevcCached;
}
